// Holiday service — stores holidays in DynamoDB via the BadgeSubmission table
// Uses formType: 'holiday' to distinguish from bookings
// Shared across all admins and users (not browser-specific)
import { API } from 'aws-amplify';
import { createBadgeSubmission, deleteBadgeSubmission } from '../graphql/mutations';
import { listBadgeSubmissions } from '../graphql/queries';

/**
 * Get all holidays from DynamoDB.
 */
export const getHolidays = async () => {
  try {
    let allItems = [];
    let nextToken = null;

    do {
      const result = await API.graphql({
        query: listBadgeSubmissions,
        variables: {
          filter: { formType: { eq: 'holiday' } },
          limit: 1000,
          nextToken
        }
      });

      const data = result.data?.listBadgeSubmissions;
      if (data?.items) {
        allItems = allItems.concat(data.items);
        nextToken = data.nextToken;
      } else {
        break;
      }
    } while (nextToken);

    // Map to the format the UI expects
    const holidays = allItems
      .filter(item => item && item.status !== 'Deleted')
      .map(item => ({
        date: item.submissionDate,
        reason: item.tokenNumber || 'Holiday',
        addedBy: item.updatedBy || item.email,
        addedAt: item.createdAt,
        id: item.id
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    return { success: true, data: holidays };
  } catch (error) {
    console.error('Error getting holidays:', error);
    return { success: false, data: [], message: error.message };
  }
};

/**
 * Add a holiday to DynamoDB.
 */
export const addHoliday = async (date, reason, adminEmail) => {
  try {
    if (!date) throw new Error('Date is required');

    // Check if holiday already exists for this date
    const existing = await getHolidays();
    if (existing.data.some(h => h.date === date)) {
      return { success: false, message: 'Holiday already exists for this date' };
    }

    const holidayId = `holiday-${date}`;

    await API.graphql({
      query: createBadgeSubmission,
      variables: {
        input: {
          id: holidayId,
          email: adminEmail || 'admin@amazon.com',
          siteCode: 'ALL',
          formType: 'holiday',
          tokenNumber: reason || 'Holiday',
          submissionDate: date,
          status: 'Active',
          updatedBy: adminEmail
        }
      }
    });

    return { success: true, message: 'Holiday added successfully' };
  } catch (error) {
    console.error('Error adding holiday:', error);
    const msg = error.errors ? error.errors.map(e => e.message).join(', ') : error.message;
    return { success: false, message: msg };
  }
};

/**
 * Remove a holiday from DynamoDB.
 */
export const removeHoliday = async (date) => {
  try {
    const holidayId = `holiday-${date}`;

    await API.graphql({
      query: deleteBadgeSubmission,
      variables: {
        input: { id: holidayId }
      }
    });

    return { success: true, message: 'Holiday removed successfully' };
  } catch (error) {
    console.error('Error removing holiday:', error);
    const msg = error.errors ? error.errors.map(e => e.message).join(', ') : error.message;
    return { success: false, message: msg };
  }
};
